// BookingCreatePage — the multi-vehicle book-in form (legacy book_in.php).
//
// Section ordering follows the Wave B3 brief:
//   1. Collection details (date / timeframe / driver / address / source)
//   2. Vehicles (1–3, accordion via VehicleForm)
//   3. Customer (search-or-new)
//   4. Shared notes
//
// Brief→server deviations (recorded in HANDOFF §Wave B3):
//   - Per-vehicle make/model/colour/payment_mode are NOT split — server
//     `vehicleInputSchema` only carries car_make_model + car_registration
//     + product_id + price + notes.
//   - Duplicate-reg endpoint is /bookings/check-duplicate?reg=&date= (the
//     dedicated route), not a `?registration=` query on /bookings.
//   - "Time slot" maps to the AM | AM-PM | PM enum (no server list).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Receipt, CalendarDays, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { DraftManagerModal } from '../../components/drafts/DraftManagerModal';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Input } from '../../components/form/Input';
import { Textarea } from '../../components/form/Textarea';
import { Select } from '../../components/form/Select';
import { DatePicker } from '../../components/form/DatePicker';
import { Checkbox } from '../../components/form/Checkbox';
import { BookingLeftPanel } from '../../components/ui/BookingLeftPanel/BookingLeftPanel';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { DuplicateBookingModal } from '../../components/ui/DuplicateBookingModal/DuplicateBookingModal';
import { Modal } from '../../components/ui/Modal/Modal';
import { DriverHolidayWarning } from '../../components/ui/DriverHolidayWarning/DriverHolidayWarning';
import {
  ImportFromEmailModal,
  type ParsedBookingFields,
} from '../../components/ui/ImportFromEmailModal/ImportFromEmailModal';
import { Tooltip } from '../../components/ui/Tooltip/Tooltip';
import { useToast } from '../../hooks/useToast';
import { useDriverHolidayCheck } from '../../hooks/useDriverHolidayCheck';
import { conflictingHolidays } from '../../lib/holidayPeriod';
import { CustomerNameAutocomplete } from './components/CustomerNameAutocomplete';
import { VehicleForm } from './components/VehicleForm';
import { BookingSuccessModal } from './components/BookingSuccessModal';
import { useBookingForm } from './hooks/useBookingForm';
import {
  useActiveDrivers,
} from './hooks/useDriversList';
import {
  useProducts,
  useWorkSources,
  usePaymentModes,
  useTimeSlots,
} from './hooks/useReferenceData';
import type { CreateBookingResponse, SlotDayType, Timeframe } from '../../types/api';
import styles from './BookingCreatePage.module.css';

function slotDayType(dateStr: string): SlotDayType {
  if (!dateStr) return 'weekday';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return 'weekday';
  const day = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

export function BookingCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [successResult, setSuccessResult] =
    useState<CreateBookingResponse | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);

  const form = useBookingForm({
    onSubmitSuccess: (response) => {
      setSuccessResult(response);
      toast.success(`Booking ${response.reference} saved. Confirmation will be sent to customer shortly.`);
    },
  });

  const { state } = form;

  const drivers = useActiveDrivers();
  const products = useProducts();
  const sources = useWorkSources();
  const paymentModes = usePaymentModes();
  const timeSlots = useTimeSlots(slotDayType(state.date));

  // Driver-holiday probe — fires whenever the selected driver and booking
  // date are both known. Modal opens automatically on conflict; staff can
  // dismiss via "Continue Anyway" without blocking submission.
  const selectedDriver = useMemo(
    () => (drivers.data ?? []).find((d) => d.id === state.driver) ?? null,
    [drivers.data, state.driver],
  );
  const holidayCheck = useDriverHolidayCheck(
    selectedDriver?.uuid ?? null,
    state.date || null,
    state.date || null,
  );
  const [holidayWarningDismissed, setHolidayWarningDismissed] = useState(false);
  // Reset the dismissal whenever the driver or date changes so a NEW
  // conflict re-opens the modal even after the staffer once chose
  // "Continue Anyway" for a different combination.
  const lastConflictKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${selectedDriver?.uuid ?? ''}|${state.date}`;
    if (lastConflictKeyRef.current !== key) {
      lastConflictKeyRef.current = key;
      setHolidayWarningDismissed(false);
    }
  }, [selectedDriver?.uuid, state.date]);
  // Only holidays that clash with the chosen timeframe warn (an AM booking
  // against a PM-only holiday does not). See lib/holidayPeriod.ts.
  const conflictingHols = useMemo(
    () => conflictingHolidays(holidayCheck.holidays, state.date, state.timeframe),
    [holidayCheck.holidays, state.date, state.timeframe],
  );
  const showHolidayWarning =
    conflictingHols.length > 0 && !holidayWarningDismissed && state.date !== '';

  function focusDriverField() {
    const el = document.querySelector<HTMLSelectElement>(
      'select[aria-label="Driver"], [data-field="driver"] select',
    );
    el?.focus();
  }

  // FIX 2 — default product_id to "End of Life Vehicle - Undepolluted"
  // (the canonical scrap-car ELV starter row) once products data lands.
  // We only set the default for vehicles that haven't picked a product
  // yet — explicit user choices are never overridden.
  const defaultProductId = useMemo(() => {
    const list = products.data ?? [];
    const undep = list.find(
      (p) => p.name === 'End of Life Vehicle - Undepolluted',
    );
    return undep?.id ?? null;
  }, [products.data]);

  useEffect(() => {
    if (defaultProductId === null) return;
    // Set the default on any vehicle with a null product_id. The effect's
    // dependencies include `state.vehicles` so newly-added vehicles also
    // get defaulted, and we guard against infinite loops by only writing
    // when the value would change.
    state.vehicles.forEach((v, i) => {
      if (v.product_id === null) {
        form.setVehicle(i, { ...v, product_id: defaultProductId });
      }
    });
  }, [defaultProductId, state.vehicles, form]);

  const driverOptions = useMemo(
    () =>
      (drivers.data ?? []).map((d) => ({ value: String(d.id), label: d.name })),
    [drivers.data],
  );
  const sourceOptions = useMemo(
    () =>
      (sources.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [sources.data],
  );
  const paymentOptions = useMemo(
    () =>
      (paymentModes.data ?? []).map((p) => ({
        value: String(p.id),
        label: p.name,
      })),
    [paymentModes.data],
  );

  const customerLinked = state.customer.customer;
  const customerFields = state.customer.fields;

  const validationErrors = useMemo(() => {
    const issues: string[] = [];
    if (!state.date) issues.push('Collection date is required.');
    if (!state.timeframe) issues.push('Time slot is required.');
    const cf = state.customer.fields;
    const haveCustomer = !!state.customer.customer;
    if (!haveCustomer && !cf.name.trim()) issues.push('Customer name is required.');
    if (!haveCustomer && !cf.phone.trim()) issues.push('Customer phone is required.');
    if (!haveCustomer && !cf.address.trim())
      issues.push('Customer address is required.');
    if (!haveCustomer && !cf.postcode.trim())
      issues.push('Customer postcode is required.');
    state.vehicles.forEach((v, i) => {
      if (!v.car_registration.trim())
        issues.push(`Vehicle ${i + 1}: registration is required.`);
      if (!v.car_make_model.trim())
        issues.push(`Vehicle ${i + 1}: make and model is required.`);
      if (v.price === '' || Number.isNaN(Number.parseFloat(v.price)))
        issues.push(`Vehicle ${i + 1}: price is required.`);
    });
    return issues;
  }, [state]);

  // FIX 6 — inline error specifically for the customer card. Surfaces the
  // "select an existing customer or fill in the new-customer fields"
  // requirement next to the Customer section, not just in the submit bar.
  const customerSectionError = useMemo<string | null>(() => {
    if (state.customer.customer !== null) return null;
    const cf = state.customer.fields;
    if (
      cf.name.trim() &&
      cf.phone.trim() &&
      cf.address.trim() &&
      cf.postcode.trim()
    ) {
      return null;
    }
    return 'Search for an existing customer or fill in the customer details below.';
  }, [state.customer]);

  const canSubmit = validationErrors.length === 0 && !form.isSubmitting;

  function handleBookAnother() {
    setSuccessResult(null);
    form.reset();
    toast.success('Form cleared. Ready for the next booking.');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    await form.submitAsync();
  }

  function applyEmailImport(parsed: ParsedBookingFields) {
    if (parsed.customer_name) form.setCustomerField('name', parsed.customer_name);
    if (parsed.phone) form.setCustomerField('phone', parsed.phone);
    if (parsed.email) form.setCustomerField('email', parsed.email);
    if (parsed.address) form.setCustomerField('address', parsed.address);
    if (parsed.postcode) form.setCustomerField('postcode', parsed.postcode);
    const v0 = state.vehicles[0];
    if (v0) {
      form.setVehicle(0, {
        ...v0,
        car_make_model: parsed.vehicle_make_model ?? v0.car_make_model,
        car_registration: parsed.vehicle_reg ?? v0.car_registration,
        price: parsed.price ?? v0.price,
        notes: parsed.notes ?? v0.notes,
      });
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Booking"
        subtitle="Create a new vehicle service booking"
        actions={
          <>
            <Button
              variant="primary"
              icon={Receipt}
              onClick={() => navigate('/collections/receipts/new')}
              disabled={form.isSubmitting}
            >
              New Receipt
            </Button>
            <Button
              variant="secondary"
              icon={FileText}
              onClick={() => setDraftsOpen(true)}
              disabled={form.isSubmitting}
            >
              Drafts
            </Button>
            <Button
              variant="secondary"
              icon={CalendarDays}
              onClick={() => navigate('/collections/diary')}
              disabled={form.isSubmitting}
            >
              Diary
            </Button>
          </>
        }
      />

      <div className={styles.layout}>
      <BookingLeftPanel
        selectedCustomer={customerLinked}
        onCustomerSelect={(c) => form.setCustomer(c)}
        onImportFromEmail={() => setImportOpen(true)}
      />
      <div className={styles.main}>

      {/* Section 1 — Collection details (shared) */}
      <Card title="Collection details">
        <div className={styles.grid2}>
          <DatePicker
            label="Collection date"
            required
            value={state.date}
            onChange={(e) => form.setField('date', e.target.value)}
          />
          <Select
            label="Time slot"
            required
            options={(timeSlots.data ?? []).map((t) => ({ value: t.label, label: t.label }))}
            value={state.timeframe}
            onChange={(e) => form.setField('timeframe', e.target.value as Timeframe)}
          />
          <Select
            label="Driver"
            options={driverOptions}
            placeholder={drivers.isLoading ? 'Loading…' : '—'}
            value={state.driver !== null ? String(state.driver) : ''}
            onChange={(e) =>
              form.setField(
                'driver',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
          />
          <Select
            label="Work source"
            options={sourceOptions}
            placeholder={
              sources.isLoading
                ? 'Loading…'
                : sourceOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={state.source !== null ? String(state.source) : ''}
            onChange={(e) =>
              form.setField(
                'source',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
          />
          <Select
            label="Payment mode"
            options={paymentOptions}
            placeholder={
              paymentModes.isLoading
                ? 'Loading…'
                : paymentOptions.length === 0
                  ? 'No options — leave blank'
                  : '—'
            }
            value={
              state.payment_mode !== null ? String(state.payment_mode) : ''
            }
            onChange={(e) =>
              form.setField(
                'payment_mode',
                e.target.value === '' ? null : Number(e.target.value),
              )
            }
          />
          <div className={styles.spacer} />
        </div>

        <div className={styles.collectionAddress}>
          <Checkbox
            checked={state.use_customer_address}
            onChange={(e) =>
              form.setField('use_customer_address', e.target.checked)
            }
            label="Collection address is the same as the customer address"
          />
          {!state.use_customer_address ? (
            <div className={styles.grid2}>
              <Input
                label="Collection address"
                value={state.collection_address}
                onChange={(e) =>
                  form.setField('collection_address', e.target.value)
                }
              />
              <Input
                label="Collection postcode"
                value={state.collection_postcode}
                onChange={(e) =>
                  form.setField('collection_postcode', e.target.value)
                }
              />
            </div>
          ) : null}
        </div>
      </Card>

      {/* Section 2 — Vehicles */}
      <Card
        title="Vehicles"
        actions={
          state.vehicles.length < 3 ? (
            <Button variant="secondary" size="sm" icon={Plus} onClick={form.addVehicle}>
              Add vehicle
            </Button>
          ) : null
        }
      >
        <div className={styles.vehicles}>
          {state.vehicles.map((v, i) => (
            <VehicleForm
              key={i}
              index={i}
              total={state.vehicles.length}
              value={v}
              products={products.data ?? []}
              productsLoading={products.isLoading}
              duplicateWarning={form.duplicateWarnings[i]}
              onChange={(next) => form.setVehicle(i, next)}
              onRemove={i === 0 ? undefined : () => form.removeVehicle(i)}
              onRegBlur={(reg) => void form.checkDuplicate(i, reg)}
            />
          ))}
        </div>
      </Card>

      {/* Section 3 — Customer (the NAME field IS the autocomplete trigger;
          legacy book_in.php parity per autocomplete.js). */}
      <Card title="Customer">
        <div className={styles.grid2}>
          <CustomerNameAutocomplete
            value={customerFields.name ?? ''}
            onChange={(v) => form.setCustomerField('name', v)}
            onSelectCustomer={(c) => form.setCustomer(c)}
            linkedCustomer={customerLinked}
            onClearLinked={() => form.setNewCustomerMode(true)}
            label="Customer name"
            required
          />
          <Input
            label="Phone"
            required
            variant="tel"
            value={customerFields.phone ?? ''}
            onChange={(e) => form.setCustomerField('phone', e.target.value)}
            disabled={customerLinked !== null}
          />
          <Input
            label="Email"
            variant="email"
            value={customerFields.email ?? ''}
            onChange={(e) => form.setCustomerField('email', e.target.value)}
            disabled={customerLinked !== null}
          />
          <Input
            label="Phone (alt)"
            variant="tel"
            value={customerFields.phone2 ?? ''}
            onChange={(e) => form.setCustomerField('phone2', e.target.value)}
            disabled={customerLinked !== null}
          />
          <Input
            label="Address"
            required
            value={customerFields.address ?? ''}
            onChange={(e) => form.setCustomerField('address', e.target.value)}
            disabled={customerLinked !== null}
          />
          <Input
            label="Postcode"
            required
            value={customerFields.postcode ?? ''}
            onChange={(e) =>
              form.setCustomerField(
                'postcode',
                e.target.value.toUpperCase(),
              )
            }
            disabled={customerLinked !== null}
          />
        </div>
        {customerSectionError !== null ? (
          <div role="alert" className={styles.customerError}>
            {customerSectionError}
          </div>
        ) : null}
      </Card>

      {/* Section 4 — Shared notes */}
      <Card title="Notes">
        <Textarea
          rows={3}
          autoResize
          value={state.notes}
          onChange={(e) => form.setField('notes', e.target.value)}
          placeholder="Notes shared across all vehicles in this booking…"
          aria-label="Booking notes"
        />
      </Card>

      {/* Section 5 — Notifications */}
      <Card title="Notifications">
        <div className={styles.toggleRow}>
          <Checkbox
            checked={state.send_email}
            onChange={(e) => form.setField('send_email', e.target.checked)}
            label="Send confirmation email"
          />
          <Checkbox
            checked={state.send_sms}
            onChange={(e) => form.setField('send_sms', e.target.checked)}
            label="Send confirmation SMS"
          />
        </div>
      </Card>

      {/* Section 6 — Reminders */}
      <Card title="Reminders">
        <div className={styles.toggleRow}>
          <Checkbox
            checked={state.reminder_email}
            onChange={(e) => form.setField('reminder_email', e.target.checked)}
            label="Email reminder (day before)"
          />
          <Checkbox
            checked={state.reminder_sms}
            onChange={(e) => form.setField('reminder_sms', e.target.checked)}
            label="SMS reminder (day before)"
          />
        </div>
      </Card>

      {/* Submit */}
      <div className={styles.submitBar}>
        <ul className={styles.errorList} aria-live="polite">
          {validationErrors.slice(0, 4).map((msg) => (
            <li key={msg}>{msg}</li>
          ))}
        </ul>
        <div className={styles.submitActions}>
          <Tooltip content="Add to pending queue — available in Wave C">
            <Button variant="secondary" disabled>
              Add to Pending
            </Button>
          </Tooltip>
          <Button
            variant="primary"
            onClick={() => void handleSubmit()}
            loading={form.isSubmitting}
            disabled={!canSubmit}
          >
            Complete booking
          </Button>
        </div>
      </div>

      {/* Resume-draft prompt */}
      <ConfirmModal
        open={form.pendingDraftPrompt !== null}
        onCancel={form.rejectDraft}
        onConfirm={form.acceptDraft}
        title="Resume saved draft?"
        confirmLabel="Resume draft"
        cancelLabel="Discard"
        message={
          <>
            We found an unsubmitted booking draft from a previous session.
            Resume it, or discard and start fresh?
          </>
        }
      />

      {/* Import-from-email modal */}
      <ImportFromEmailModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApply={(p) => applyEmailImport(p)}
      />

      {/* Saved drafts browser */}
      <DraftManagerModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        draftType="booking"
        drafts={form.drafts}
        isDirty={form.isDirty}
        isSaving={form.isSavingDraft}
        onRefresh={form.listDrafts}
        onLoad={form.loadDraft}
        onDelete={form.deleteDraft}
        onSaveCurrent={form.saveDraft}
      />

      {/* Duplicate-registration modal — opens on reg blur when matches found */}
      <DuplicateBookingModal
        open={form.duplicateModal !== null}
        registration={form.duplicateModal?.registration ?? ''}
        bookings={form.duplicateModal?.bookings ?? []}
        pending={form.duplicateModal?.pending ?? []}
        onContinue={form.dismissDuplicateModal}
        onClear={() => {
          if (form.duplicateModal) {
            form.clearVehicleRegistration(form.duplicateModal.index);
          }
        }}
        onViewMatch={(m) => {
          if (m.source === 'bookings') {
            navigate(`/collections/bookings/${encodeURIComponent(m.enc_id)}`);
          } else {
            navigate(`/collections/pending?highlight=${encodeURIComponent(m.enc_id)}`);
          }
        }}
      />

      {/* Success modal */}
      <BookingSuccessModal
        open={successResult !== null}
        result={successResult}
        driverName={
          drivers.data?.find((d) => d.id === state.driver)?.name ?? null
        }
        vehicleCount={
          successResult ? 1 + (successResult.vehicles?.length ?? 0) : 0
        }
        onClose={() => setSuccessResult(null)}
        onBookAnother={handleBookAnother}
      />

      {/* Same-day duplicate registration (server 409) — a warning staff can confirm. */}
      <Modal
        open={form.duplicateConfirm !== null}
        onClose={form.cancelDuplicateConfirm}
        title="Duplicate registration"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={form.cancelDuplicateConfirm}>
              Go Back
            </Button>
            <Button variant="primary" onClick={form.confirmDuplicateAndSubmit}>
              Book Anyway
            </Button>
          </>
        }
      >
        <p>{form.duplicateConfirm?.message}</p>
        <p>Do you want to create this booking anyway?</p>
      </Modal>

      {/* Driver-holiday conflict warning — non-blocking; staff can proceed. */}
      <DriverHolidayWarning
        isOpen={showHolidayWarning}
        onClose={() => setHolidayWarningDismissed(true)}
        onContinueAnyway={() => setHolidayWarningDismissed(true)}
        onChangeDriverOrDate={() => {
          setHolidayWarningDismissed(true);
          focusDriverField();
        }}
        driverName={selectedDriver?.name ?? ''}
        bookingDate={state.date}
        holidays={conflictingHols}
      />
      </div>
      </div>
    </div>
  );
}
